/// <reference types="vite/client" />
import posthog from 'posthog-js';

const isProduction = import.meta.env.VITE_PUBLIC_POSTHOG_ENV === 'production';
const isDevelopment = import.meta.env.DEV;
const isLocalhost = typeof window !== 'undefined' && 
  (location.hostname === 'localhost' || location.hostname === '127.0.0.1');

export const initPostHog = () => {
  if (typeof window === 'undefined') return null;
  
  // Conditional debug logging for E2E tests
  if (import.meta.env.VITE_POSTHOG_DEBUG || isLocalhost) {
    console.log('[PostHog] Initializing...');
    console.log('[PostHog] Env:', import.meta.env.MODE);
    console.log('[PostHog] Key exists:', !!import.meta.env.VITE_PUBLIC_POSTHOG_KEY);
  }

  // Option 1: Completely disable in development if no key is present
  if (isDevelopment && !import.meta.env.VITE_PUBLIC_POSTHOG_KEY) {
    console.log('[PostHog] Disabled in development (no key)');
    return null;
  }
  
  posthog.init(import.meta.env.VITE_PUBLIC_POSTHOG_KEY, {
    api_host: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
    
    // Core settings
    autocapture: true,
    capture_pageview: true,
    capture_pageleave: true,
    
    // Person profiles
    person_profiles: 'identified_only',
    
    // Error tracking
    capture_exceptions: {
      capture_unhandled_errors: true,
      capture_unhandled_rejections: true,
      capture_console_errors: false
    },
    
    // Session replay - disable in dev to save quota
    disable_session_recording: isDevelopment,
    
    loaded: (posthog) => {
      const env = import.meta.env.VITE_PUBLIC_POSTHOG_ENV || (isLocalhost ? 'localhost' : 'development');
      
      // Enable debug mode in development
      if (isDevelopment) {
        posthog.debug(true);
      }
      
      // 1. Register as a Super Property (sent with every event)
      posthog.register({
        environment: env,
        app_version: __APP_VERSION__,
      });

      // 2. Also set it on the person profile for easy filtering in the "Persons" tab
      // Note: This will only be sent once the user is identified, but good to have queued.
      posthog.setPersonPropertiesForFlags({
        environment: env
      });
    }
  });
  
  return posthog;
};


export { posthog };

interface QueuedEvent {
  event: string;
  properties: Record<string, any>;
  timestamp: string;
}

let offlineQueue: QueuedEvent[] = [];

// Initialize from localStorage if available
if (typeof window !== 'undefined') {
  try {
    const stored = localStorage.getItem('posthog_offline_queue');
    if (stored) {
      offlineQueue = JSON.parse(stored);
    }
  } catch (e) {
    console.error('Failed to load offline queue:', e);
  }
}

export function captureWithOfflineSupport(
  event: string,
  properties: Record<string, any> = {}
) {
  if (navigator.onLine && posthog) {
    posthog.capture(event, properties);
  } else {
    // Always read latest state from storage to handle reloads and multi-tab
    try {
      const stored = localStorage.getItem('posthog_offline_queue');
      if (stored) {
        offlineQueue = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to load offline queue:', e);
    }

    const queuedEvent: QueuedEvent = {
      event,
      properties,
      timestamp: new Date().toISOString()
    };
    offlineQueue.push(queuedEvent);
    try {
      localStorage.setItem('posthog_offline_queue', JSON.stringify(offlineQueue));
    } catch (e) {
      console.error('Failed to save offline queue:', e);
    }
  }
}

export function initOfflineSync() {
  if (typeof window === 'undefined') return () => {};

  const flushQueue = () => {
    // Re-read from storage to ensure we have the latest state (in case of multiple tabs)
    try {
      const stored = localStorage.getItem('posthog_offline_queue');
      if (stored) {
        offlineQueue = JSON.parse(stored);
      }
    } catch (e) {
      console.error('Failed to sync offline queue:', e);
    }

    if (offlineQueue.length > 0 && posthog) {
      offlineQueue.forEach(({ event, properties, timestamp }) => {
        posthog.capture(event, {
          ...properties,
          _captured_offline: true,
          _original_timestamp: timestamp
        });
      });
      // Clear both memory and storage
      offlineQueue = [];
      localStorage.removeItem('posthog_offline_queue');
    }
  };

  window.addEventListener('online', flushQueue);
  
  // Try to flush on init if online
  if (navigator.onLine) {
    flushQueue();
  }

  return () => {
    window.removeEventListener('online', flushQueue);
  };
}

export const captureSilentError = (context: string, error: any) => {
    console.error(`[${context}]`, error);
    if (posthog) {
         posthog.capture('silent_error', {
            context,
            error: error?.message || String(error),
            stack: error?.stack,
        });
    }
};
